import z, { useZodForm } from '@/lib/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { REGEXP_ONLY_DIGITS } from 'input-otp';
import { AlertCircle, Check, CheckCircle, Copy, Loader } from 'lucide-react';
import React from 'react';

import RevokeMfa from '@/components/mfa/mfa-totp-revoke';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/ui/dialog';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@/components/ui/form';
import {
    InputOTP,
    InputOTPGroup,
    InputOTPSlot,
} from '@/components/ui/input-otp';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuthContext } from '@/context/auth-provider';
import { toast } from '@/hooks/use-toast';
import { mfaTotpSetupQueryFn, verifyMfaTotpMutationFn } from '@/lib/api';
import { MFATotpEnableSchema } from '@/schemas';
import { Separator } from '../ui/separator';

type TMFA_TOTP = {
    message: string;
    secret: string;
    qrImageUrl: string;
};

const EnableTotpFA = () => {
    const queryClient = useQueryClient();
    const { totpFA } = useAuthContext();
    const [showKey, setShowKey] = React.useState(false);
    const [isOpen, setIsOpen] = React.useState(false);

    const [copied, setCopied] = React.useState(false);

    const { data, refetch, isLoading } = useQuery({
        queryKey: ['mfa-setup'],
        queryFn: mfaTotpSetupQueryFn,
        enabled: isOpen,
        staleTime: Infinity,
    });

    const { mutate, isPending } = useMutation({
        mutationFn: verifyMfaTotpMutationFn,
    });

    const mfaData = data ?? ({} as TMFA_TOTP);

    const otpCellStyle = '[@media(min-width:440px)]:!w-14 !h-12 !text-lg';

    const totpForm = useZodForm(MFATotpEnableSchema, {
        defaultValues: {
            code: '',
        },
    });

    const onSubmit = (values: z.infer<typeof MFATotpEnableSchema>) => {
        const data = {
            code: values.code,
            secretKey: mfaData.secret,
        };
        mutate(data, {
            onSuccess: (response) => {
                queryClient.invalidateQueries({
                    queryKey: ['authUser'],
                });
                setIsOpen(false);
                toast({
                    title: 'Success',
                    description: response?.data.message,
                });
            },
            onError: (error) => {
                toast({
                    title: 'Error',
                    description: error.message,
                    variant: 'destructive',
                });
            },
        });
    };

    const onCopy = React.useCallback((value: string) => {
        navigator.clipboard.writeText(value);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    }, []);

    return (
        <>
            <div className='flex flex-col gap-5 my-4'>
                <div className='relative sm:pl-6 transition duration-200 ease-in-out'>
                    <div className='bg-white dark:bg-background  absolute -left-[9.5px] top-[2.1rem] z-10 block size-6 sm:size-5 rounded-full'>
                        <div className='ml-1 mt-1 size-4 sm:size-3 rounded-full border-2 transition duration-200 ease-in-out border-primary'></div>
                    </div>
                    <div>
                        <div className='via-root to-root rounded-xl bg-gradient-to-r p-0.5'>
                            <div className='rounded-[10px] bg-warning/25 p-6'>
                                <div className='flex items-center gap-3'>
                                    <h3 className='sm:text-lg tracking-[-0.16px] text-slate-12 font-bold mb-1'>
                                        Time-based One-Time Password
                                        Authentication (TOTP)
                                    </h3>
                                    {totpFA.isEnabled && (
                                        <span
                                            className='select-none whitespace-nowrap font-medium bg-success text-white
          text-xs h-6 px-2 rounded flex flex-row items-center justify-center gap-1'>
                                            Enabled
                                        </span>
                                    )}
                                </div>
                                <p className='mb-6 text-sm text-[#0007149f] dark:text-gray-100 font-normal'>
                                    By generating an OTP (One Time Password) by
                                    Authenticator application.
                                </p>

                                <p
                                    className={`md:max-w-max text-[0.8rem] ${totpFA.isEnabled ? 'text-white bg-success' : 'text-warning bg-warning/15'} my-4 p-2 rounded-md flex flex-col md:flex-row gap-2 text-center md:text-start items-center`}>
                                    {totpFA.isEnabled ? (
                                        <>
                                            <CheckCircle size={20} />
                                            <span className='font-bold'>
                                                Secure:
                                            </span>{' '}
                                            Your account is protected.
                                        </>
                                    ) : (
                                        <>
                                            <AlertCircle size={20} />
                                            <span className='font-bold'>
                                                Warning:
                                            </span>{' '}
                                            Enable TOTP 2FA today → it takes 2
                                            minutes and can save you from
                                            permanent lockout tomorrow.
                                        </>
                                    )}
                                </p>
                                {totpFA.isEnabled ? (
                                    <RevokeMfa />
                                ) : (
                                    <Dialog
                                        modal
                                        open={isOpen}
                                        onOpenChange={(open) => {
                                            setIsOpen((prev) => !prev);
                                            totpForm.reset();
                                            if (open) {
                                                refetch({ throwOnError: true });
                                            }
                                        }}>
                                        <DialogTrigger asChild>
                                            <Button
                                                className='h-[2.5rem] w-[7rem] py-1'
                                                variant={'outline'}>
                                                Enable MFA
                                            </Button>
                                        </DialogTrigger>
                                        <DialogContent className='!gap-0'>
                                            <DialogHeader>
                                                <DialogTitle className='text-[17px] text-slate-12 font-semibold'>
                                                    Setup Multi-Factor
                                                    Authentication (TOTP)
                                                </DialogTitle>
                                            </DialogHeader>
                                            <div className='w-full'>
                                                <p className='mt-6 text-sm text-[#0007149f] dark:text-inherit font-bold'>
                                                    Scan the QR code
                                                </p>
                                                <span className='text-sm text-[#0007149f] dark:text-inherit font-normal'>
                                                    Use an app like{' '}
                                                    <a
                                                        className='!text-primary underline decoration-primary decoration-1 underline-offset-2 transition duration-200 ease-in-out hover:decoration-blue-11 dark:text-current dark:decoration-slate-9 dark:hover:decoration-current '
                                                        rel='noopener noreferrer'
                                                        target='_blank'
                                                        href='https://support.1password.com/one-time-passwords/'>
                                                        1Password
                                                    </a>{' '}
                                                    or{' '}
                                                    <a
                                                        className='!text-primary underline decoration-primary decoration-1 underline-offset-2 transition duration-200 ease-in-out hover:decoration-blue-11 dark:text-current dark:decoration-slate-9 dark:hover:decoration-current '
                                                        rel='noopener noreferrer'
                                                        target='_blank'
                                                        href='https://safety.google/authentication/'>
                                                        Google Authenticator
                                                    </a>{' '}
                                                    to scan the QR code below.
                                                </span>
                                            </div>
                                            <div className='mt-4 flex flex-col md:flex-row items-center gap-4'>
                                                <div className='shrink-0 rounded-md border p-2  border-[#0009321f] dark:border-gray-600 bg-white dark:bg-inherit'>
                                                    {isLoading ||
                                                    !mfaData?.qrImageUrl ? (
                                                        <Skeleton className='w-[160px] h-[160px]' />
                                                    ) : (
                                                        <img
                                                            alt='QR code'
                                                            src={
                                                                mfaData?.qrImageUrl ||
                                                                ''
                                                            }
                                                            decoding='async'
                                                            width='160'
                                                            height='160'
                                                            className='rounded-md w-44'
                                                        />
                                                    )}
                                                </div>

                                                {showKey ? (
                                                    <div className='w-full !text-[0.8rem]'>
                                                        <div
                                                            className='flex items-center justify-center gap-1
                                               sm:text-sm text-[#0007149f] dark:text-muted-foreground font-normal mb-4'>
                                                            <span>
                                                                Copy setup key
                                                            </span>
                                                            <button
                                                                onClick={() =>
                                                                    onCopy(
                                                                        mfaData.secret,
                                                                    )
                                                                }>
                                                                {copied ? (
                                                                    <Check className='w-4 h-4' />
                                                                ) : (
                                                                    <Copy className='w-4 h-4' />
                                                                )}
                                                            </button>
                                                        </div>

                                                        {isLoading &&
                                                        !mfaData.secret ? (
                                                            <Skeleton className='w-[200px] h-2' />
                                                        ) : (
                                                            <p className='text-sm block !text-center truncate max-w-[200px] mx-auto text-black dark:text-primary'>
                                                                {mfaData.secret}
                                                            </p>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <span className='text-[0.8rem] sm:text-sm text-[#0007149f] dark:text-muted-foreground font-normal'>
                                                        Can&apos;t scan the
                                                        code?
                                                        <button
                                                            className='block text-primary transition duration-200 ease-in-out hover:underline
                                            dark:text-white'
                                                            type='button'
                                                            onClick={() =>
                                                                setShowKey(true)
                                                            }>
                                                            View the Setup Key
                                                        </button>
                                                    </span>
                                                )}
                                            </div>

                                            <div className='mt-8 border-t'>
                                                <Form {...totpForm}>
                                                    <form
                                                        onSubmit={totpForm.handleSubmit(
                                                            onSubmit,
                                                        )}
                                                        className='sm:w-full mt-6 flex flex-col gap-4 '>
                                                        <FormField
                                                            control={
                                                                totpForm.control
                                                            }
                                                            name='code'
                                                            render={({
                                                                field,
                                                            }) => (
                                                                <FormItem className='flex flex-col items-center '>
                                                                    <FormLabel className='text-sm mb-1 text-slate-11 font-bold'>
                                                                        Then
                                                                        enter
                                                                        the code
                                                                    </FormLabel>
                                                                    <FormControl>
                                                                        <InputOTP
                                                                            className='!text-lg flex !flex-col sm:!flex-row items-center'
                                                                            maxLength={
                                                                                6
                                                                            }
                                                                            pattern={
                                                                                REGEXP_ONLY_DIGITS
                                                                            }
                                                                            {...field}
                                                                            style={{
                                                                                justifyContent:
                                                                                    'center',
                                                                            }}>
                                                                            <InputOTPGroup>
                                                                                {Array.from(
                                                                                    {
                                                                                        length: 3,
                                                                                    },
                                                                                    (
                                                                                        _,
                                                                                        i,
                                                                                    ) => (
                                                                                        <InputOTPSlot
                                                                                            key={
                                                                                                i
                                                                                            }
                                                                                            index={
                                                                                                i
                                                                                            }
                                                                                            className={
                                                                                                otpCellStyle
                                                                                            }
                                                                                        />
                                                                                    ),
                                                                                )}
                                                                            </InputOTPGroup>

                                                                            <Separator className='w-6 bg-primary' />

                                                                            <InputOTPGroup>
                                                                                {Array.from(
                                                                                    {
                                                                                        length: 3,
                                                                                    },
                                                                                    (
                                                                                        _,
                                                                                        i,
                                                                                    ) => (
                                                                                        <InputOTPSlot
                                                                                            key={
                                                                                                i
                                                                                            }
                                                                                            index={
                                                                                                i +
                                                                                                3
                                                                                            }
                                                                                            className={
                                                                                                otpCellStyle
                                                                                            }
                                                                                        />
                                                                                    ),
                                                                                )}
                                                                            </InputOTPGroup>
                                                                        </InputOTP>
                                                                    </FormControl>
                                                                    <FormMessage />
                                                                </FormItem>
                                                            )}
                                                        />
                                                        <Button
                                                            disabled={
                                                                isPending ||
                                                                isLoading
                                                            }
                                                            className='w-full h-[40px]'>
                                                            {isPending && (
                                                                <Loader className='animate-spin' />
                                                            )}
                                                            Verify
                                                        </Button>
                                                    </form>
                                                </Form>
                                            </div>
                                        </DialogContent>
                                    </Dialog>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default EnableTotpFA;
